import { useFormik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { useState, useRef, useEffect } from "react";
import ComplaintService from "../../services/complaint/ComplaintService";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useNavigate } from "react-router-dom";
import { userOption, userResult } from '../../models/orders/order-list-model';
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { ImageTemplateDemo } from "../images/file-upload-template";
import { Image } from 'primereact/image';

interface EditComplaintProps {
  isUpdate: boolean;
  handleOnSuccess: () => void;
  updateComplaintInfo: any;
  AddedData: any;
  selectedComplaint?: any;
  complaintType?: 'product' | 'service';
}

const ComplaintActions: React.FC<EditComplaintProps> = ({ 
  isUpdate, 
  handleOnSuccess, 
  updateComplaintInfo,
  selectedComplaint,
  complaintType = 'product'
}) => {
  const [imageFile, setImageFile] = useState<any>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [forceResolved, setForceResolved] = useState(false);
  const toast = useRef<Toast>(null);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const navigate = useNavigate();
  const blankUserDetail: userOption[] = [{ userID: 0, name: '', primaryContactNbr: '' }];
  const [adminUser, setAdminUser] = useState<userResult[]>([]);
  const [adminUsers, setAdminUsers] = useState<userOption[]>(blankUserDetail);
  const [selectedUser, setSelectedUser] = useState<any>();
  const [containerType, setContainerType] = useState<string>("Complaints");

  useEffect(() => {
    if (adminUsers && adminUsers.length > 0) {
      setAdminUser(adminUsers.map((user) => {
        return {
          userID: user.userID,
          detail: user.userID !== 0 ? `${user.name} , ${user.primaryContactNbr}` : "",
        };
      }));
    }
  }, [adminUsers]);

  useEffect(() => {
    if (selectedComplaint) {
      formik.setValues({
        id: null,
        productComplaintId: complaintType === 'product' ? selectedComplaint.id : null,
        serviceComplaintId: complaintType === 'service' ? selectedComplaint.id : null,
        description: selectedComplaint.description || '',
        image: selectedComplaint.image || null,
        remarks: selectedComplaint.remarks || '',
        severity: selectedComplaint.severity || '',
        correctiveAction: selectedComplaint.correctiveAction || '',
        preventiveAction: selectedComplaint.preventiveAction || '',
        actionBy: selectedComplaint.actionById || null,
        statusId: selectedComplaint.statusId || 1,
        customerSupportAction: selectedComplaint.customerSupportAction || '',
        actionDate: new Date().toISOString().split('T')[0],
        copqInRs: selectedComplaint.copqInRs || null,
        containerType: containerType
      });
    }
  }, [selectedComplaint, complaintType]);

  const handleAdminDropdownChange = async (e: DropdownChangeEvent, setFieldValue: any) => {
    const selectedUser = e.value;
    setSelectedUser(selectedUser);
    setFieldValue("actionBy", selectedUser); 
  };

  const handleAdminDropdownFilter = async (e: DropdownFilterEvent) => {
    if (e.filter.length > 2) {
      try {
        const response = await ComplaintService.getAdminUsers(e.filter);
        if (response && response.length > 0) {
          setAdminUsers(response);
        } else {
          setAdminUsers(blankUserDetail);
        }
      } catch (error) {
        console.error("Error fetching admin users:", error);
        setAdminUsers(blankUserDetail);
      }
    }
  };
    
  const severityOptions = [
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' }
  ];

  const statusOptions = [
    { label: 'New', value: 1 },
    { label: 'In Progress', value: 2 },
    { label: 'Resolved', value: 3 }
  ];

  const validationSchema = Yup.object({
    description: Yup.string().required("Description is required"),
    actionBy: Yup.number().required("Action by is required").min(1, "Please select an admin"),
    statusId: Yup.number().required("Status is required")
  });

  const formik = useFormik({
    initialValues: {
      id: null,
      productComplaintId: null,
      serviceComplaintId: null,
      description: '',
      image: [],
      remarks: '',
      severity: '',
      correctiveAction: '',
      preventiveAction: '',
      actionBy: null,
      statusId: 1,
      customerSupportAction: '',
      actionDate: new Date().toISOString().split('T')[0],
      copqInRs: null,
      containerType: containerType
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (isSubmitted) return;
      setIsSubmitted(true);

      try {
        const formData = {
          ...values,
          productComplaintId: complaintType === 'product' ? values.productComplaintId : null,
          serviceComplaintId: complaintType === 'service' ? values.serviceComplaintId : null,
          image: imageFile,
        };

        const response = await ComplaintService.CreateComplaintAction(formData);
        if (response.isSuccess) {
          showMessage('Success', 'Complaint action created successfully', 'success');
          handleOnSuccess();
          formik.resetForm();
          setImageFile(null);
          setForceResolved(false);
          navigate('/complaint-actions-main'); 
        } else {
          showMessage('Error', response.message, 'error');
        }
      } catch (error) {
        console.error("Error creating complaint action:", error);
        showMessage('Error', 'Failed to create complaint action', 'error');
      } finally {
        setIsSubmitted(false);
      }
    }
  });

  useEffect(() => {
    if (forceResolved) {
      formik.setFieldValue('statusId', 3); 
      return;
    }

    const correctiveFilled = formik.values.correctiveAction.trim() !== '';
    const preventiveFilled = formik.values.preventiveAction.trim() !== '';

    if (correctiveFilled && preventiveFilled) {
      formik.setFieldValue('statusId', 3); 
    } else if (correctiveFilled || preventiveFilled) {
      formik.setFieldValue('statusId', 2); 
    } else {
      formik.setFieldValue('statusId', 1); 
    }
  }, [formik.values.correctiveAction, formik.values.preventiveAction, forceResolved]);

  const showMessage = (label: string, message: string, severity: 'success' | 'error' | 'info') => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const fieldHoverStyle = (fieldName: string) => ({
    border: hoveredField === fieldName ? '2px solid #6366F1' : '1px solid #ced4da',
    borderRadius: '6px',
    transition: 'border 0.2s ease-in-out',
    width: '100%',
    minHeight: '40px'
  });

  const handelfileUpload = (query: any) => {
    const file = query;
    setImageFile(file);
  }

  const handelfileSizeExceeded = (message: string) => {
    showMessage('Info', message, 'info');
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="form-container px-3 md:px-4 lg:px-5 py-3">
        <div className="card p-0 border-round w-full">
          <div className="p-3">
            <h5 className="border-round mb-3 order-top-color p-2 panel-header-text text-center shadow-3">
              Complaint Actions
            </h5>

            <form onSubmit={formik.handleSubmit}>
              <div className="p-fluid formgrid grid">
                {/* Description */}
                <div className="field col-12 mb-3">
                  <label htmlFor="description" style={{ fontWeight: "bold" }}>
                    Description <span style={{ color: "red" }}>*</span>
                  </label>
                  <div
                    onMouseEnter={() => setHoveredField("description")}
                    onMouseLeave={() => setHoveredField(null)}
                    style={fieldHoverStyle("description")}
                  >
                    <InputTextarea
                      id="description"
                      name="description"
                      rows={5}
                      className="w-full"
                      value={formik.values.description}
                      readOnly
                      style={{
                        border: "none",
                        outline: "none",
                        width: "100%",
                        padding: "0.75rem",
                        fontSize: "1rem",
                        backgroundColor: "#f5f5f5",
                        cursor: "not-allowed"
                      }}
                    />

                  </div>
                  {formik.errors.description && formik.touched.description && (
                    <small className="p-error">{formik.errors.description}</small>
                  )}
                </div>

                {/* File Uploads */}
                <div className="field col-12 mb-3">
                  <ImageTemplateDemo onFileUpload={handelfileUpload} handelFileSize={handelfileSizeExceeded} />
                </div>

                {/* Preview Uploaded Files */}
                {formik.values.image?.map((x: any, index: number) => {
                  const fileUrl = x.fileDetails?.url;
                  const fileType = x.fileDetails?.type?.toLowerCase() || "";
                  const fileName = x.fileDetails?.name || `File-${index + 1}`;

                  const isImage = fileType.startsWith("image");
                  const isPDF = fileType === "application/pdf";
                  const isVideo = fileType.startsWith("video");
                  const isExcel = fileType.includes("spreadsheet") || fileType.includes("excel");
                  const isWord = fileType.includes("word");
                  const isZip = fileType.includes("zip") || fileType.includes("compressed");

                  const iconClass = isExcel
                    ? "pi pi-file-excel"
                    : isWord
                      ? "pi pi-file-word"
                      : isZip
                        ? "pi pi-file-archive"
                        : "pi pi-file";

                  return (
                    <div key={index} className="col-12 sm:col-12 md:col-6 lg:col-4 mb-3">
                      <div className="card text-center p-3">
                        {isImage && <Image src={fileUrl} alt={fileName} width="250" preview />}
                        {isVideo && (
                          <video controls width="100%" height="240">
                            <source src={fileUrl} type="video/mp4" />
                          </video>
                        )}
                        {isPDF && (
                          <>
                            <embed src={fileUrl} type="application/pdf" width="100%" height="300px" className="border-round border-1 surface-border" />
                            <div className="mt-2">{fileName}</div>
                            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="p-button p-button-sm p-button-text mt-2">
                              Open PDF
                            </a>
                          </>
                        )}
                        {!isImage && !isPDF && !isVideo && (
                          <>
                            <i className={iconClass} style={{ fontSize: "3rem", color: "#6b7280" }}></i>
                            <div className="mt-2">{fileName}</div>
                            <a href={fileUrl} download className="p-button p-button-sm p-button-outlined mt-2">
                              Download
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Remarks */}
                <div className="field col-12 mb-3">
                  <label htmlFor="remarks" style={{ fontWeight: "bold" }}>Remarks</label>
                  <div onMouseEnter={() => setHoveredField("remarks")} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle("remarks")}>
                    <InputTextarea
                      id="remarks"
                      name="remarks"
                      rows={3}
                      className="w-full"
                      value={formik.values.remarks}
                      readOnly
                      style={{
                        border: "none",
                        outline: "none",
                        padding: "0.75rem",
                        backgroundColor: "#f5f5f5",
                        cursor: "not-allowed"
                      }}
                    />

                  </div>
                </div>

                {/* Corrective / Preventive Actions */}
                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label htmlFor="correctiveAction" style={{ fontWeight: "bold" }}>Corrective Action</label>
                    <div onMouseEnter={() => setHoveredField("correctiveAction")} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle("correctiveAction")}>
                      <InputTextarea
                        id="correctiveAction"
                        name="correctiveAction"
                        rows={4}
                        className="w-full"
                        value={formik.values.correctiveAction}
                        onChange={(e) => formik.setFieldValue("correctiveAction", e.target.value.replace(/\s\s+/g, " "))}
                        style={{ border: "none", outline: "none", padding: "0.75rem", fontSize: "1rem" }}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label htmlFor="preventiveAction" style={{ fontWeight: "bold" }}>Preventive Action</label>
                    <div onMouseEnter={() => setHoveredField("preventiveAction")} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle("preventiveAction")}>
                      <InputTextarea
                        id="preventiveAction"
                        name="preventiveAction"
                        rows={4}
                        className="w-full"
                        value={formik.values.preventiveAction}
                        onChange={(e) => formik.setFieldValue("preventiveAction", e.target.value.replace(/\s\s+/g, " "))}
                        style={{ border: "none", outline: "none", padding: "0.75rem", fontSize: "1rem" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Support Action */}
                <div className="field col-12 mb-3">
                  <label htmlFor="customerSupportAction" style={{ fontWeight: "bold" }}>Customer Support Action</label>
                  <div onMouseEnter={() => setHoveredField("customerSupportAction")} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle("customerSupportAction")}>
                    <InputTextarea
                      id="customerSupportAction"
                      name="customerSupportAction"
                      rows={4}
                      className="w-full"
                      value={formik.values.customerSupportAction}
                      readOnly
                      style={{
                        border: "none",
                        outline: "none",
                        padding: "0.75rem",
                        backgroundColor: "#f5f5f5",
                        cursor: "not-allowed"
                      }}
                    />

                  </div>
                </div>

                {/* Actioned By & Status */}
                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label htmlFor="actionBy" style={{ fontWeight: "bold" }}>
                      Actioned By <span style={{ color: "red" }}>*</span>
                    </label>
                    <div onMouseEnter={() => setHoveredField("userId")} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle("userId")}>
                      <Dropdown
                        placeholder="Select Admin User"
                        id="actionBy"
                        name="actionBy"
                        options={adminUser}
                        optionLabel="detail"
                        optionValue="userID"
                        value={formik.values.actionBy}
                        onChange={(e: DropdownChangeEvent) => handleAdminDropdownChange(e, formik.setFieldValue)}
                        onFilter={handleAdminDropdownFilter}
                        filter
                        className="w-full"
                        style={{ border: 'none', outline: 'none' }}
                      />
                    </div>
                    {formik.errors.actionBy && formik.touched.actionBy && (
                      <small className="p-error">{formik.errors.actionBy as string}</small>
                    )}
                  </div>
                </div>

                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label htmlFor="statusId" style={{ fontWeight: 'bold' }}>
                      Status <span style={{ color: 'red' }}>*</span>
                    </label>
                    <div
                      onMouseEnter={() => setHoveredField('statusId')}
                      onMouseLeave={() => setHoveredField(null)}
                      style={fieldHoverStyle('statusId')}
                    >
                      <Dropdown
                        id="statusId"
                        name="statusId"
                        options={statusOptions}
                        optionLabel="label"
                        optionValue="value"
                        value={formik.values.statusId}
                        onChange={formik.handleChange}
                        className="w-full"
                        style={{ border: 'none', outline: 'none' }}
                        disabled={forceResolved}
                      />
                    </div>
                    <div className="flex align-items-center mt-2">
                      <Checkbox
                        inputId="forceResolved"
                        checked={forceResolved}
                        onChange={(e) => {
                          setForceResolved(e.checked ?? false);
                          if (e.checked) {
                            formik.setFieldValue('statusId', 3);
                          }
                        }}
                      />
                      <label htmlFor="forceResolved" className="ml-2" style={{ fontWeight: 'normal' }}>
                        Mark as Resolved
                      </label>
                    </div>
                    {formik.errors.statusId && formik.touched.statusId && (
                      <small className="p-error">{formik.errors.statusId}</small>
                    )}
                  </div>
                </div>

                {/* Action Date */}
                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label htmlFor="actionDate" style={{ fontWeight: 'bold' }}>Action Date</label>
                    <div
                      onMouseEnter={() => setHoveredField('actionDate')}
                      onMouseLeave={() => setHoveredField(null)}
                      style={fieldHoverStyle('actionDate')}
                    >
                      <InputText
                        type="date"
                        name="actionDate"
                        id="actionDate"
                        value={formik.values.actionDate}
                        onChange={formik.handleChange}
                        onFocus={(e) => e.target.showPicker?.()}
                        onKeyDown={(e) => e.preventDefault()}
                        className={`w-full ${classNames({ 'p-invalid': formik.errors.actionDate })}`}
                        style={{ border: 'none', outline: 'none' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Severity */}
                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label htmlFor="severity" style={{ fontWeight: 'bold' }}>Severity</label>
                    <div
                      onMouseEnter={() => setHoveredField('severity')}
                      onMouseLeave={() => setHoveredField(null)}
                      style={fieldHoverStyle('severity')}
                    >
                      <Dropdown
                        id="severity"
                        name="severity"
                        options={severityOptions}
                        optionLabel="label"
                        optionValue="value"
                        value={formik.values.severity}
                        onChange={formik.handleChange}
                        className="w-full"
                        style={{ border: 'none', outline: 'none' }}
                      />
                    </div>
                  </div>
                </div>

                {/* COPQ in Rs */}
                <div className="col-12 md:col-6">
                  <div className="field mb-3">
                    <label htmlFor="copqInRs" style={{ fontWeight: 'bold' }}>COPQ in Rs</label>
                    <div
                      onMouseEnter={() => setHoveredField('copqInRs')}
                      onMouseLeave={() => setHoveredField(null)}
                      style={fieldHoverStyle('copqInRs')}
                    >
                      <InputNumber
                        placeholder="Enter COPQ in RS"
                        name="copqInRs"
                        id="copqInRs"
                        className="w-full"
                        value={formik.values.copqInRs !== null ? formik.values.copqInRs : null}
                        onValueChange={(e) => {
                          if (e.value !== null && e.value !== undefined && e.value >= 0) {
                            formik.setFieldValue("copqInRs", e.value);
                          }
                        }}
                        locale="en-IN"
                        maxFractionDigits={2}
                        style={{ border: 'none', outline: 'none' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="field col-12 flex flex-wrap justify-content-end gap-3">
                  <Button
                    type="button"
                    label="Reset"
                    className="p-button-secondary"
                    onClick={() => {
                      formik.resetForm();
                      setImageFile(null);
                      setForceResolved(false);
                    }}
                  />
                  <Button
                    type="submit"
                    label="Save Complaint Action"
                    className="p-button-primary"
                    disabled={isSubmitted}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>


    </>
  );
};

export default ComplaintActions;